/* ============================================================================
 * DailyRemindersCard — Settings › Notifications (owner).
 *
 * Morning / lunch / end-of-day reminders for the owner and the crew
 * (migration 115). The schedule lives in settings key `reminders`; the
 * server decides what each reminder says. This card sets the schedule, turns
 * on notifications for this device, sets up the owner's text backup, and
 * shows which crew members have reminders on.
 * ========================================================================== */

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { BellRing, Loader2, Save, Smartphone, MessageSquareText, Mail } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import {
  DEFAULT_REMINDERS,
  currentPushSubscription,
  disablePushOnThisDevice,
  enablePushOnThisDevice,
  fetchPushPublicKey,
  fetchReminderOverview,
  fetchReminderSettings,
  fetchSummaryEmail,
  pushSupport,
  saveOwnerDevice,
  saveReminderSettings,
  setOwnerSms,
  setSummaryEmail,
} from '@/lib/reminders';

const TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
];
const DAYS = [1, 2, 3, 4, 5, 6, 7];
const SLOTS = ['morning', 'lunch', 'evening'];

const Row = ({ label, children, testId }) => (
  <div
    className="flex items-center justify-between gap-3 border border-border rounded-md bg-[hsl(var(--muted))]/40 px-3 py-2.5"
    data-testid={testId}
  >
    <span className="text-sm text-foreground">{label}</span>
    {children}
  </div>
);

const Small = ({ children }) => (
  <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{children}</Label>
);

export default function DailyRemindersCard() {
  const { t } = useTranslation();
  const [cfg, setCfg] = useState(DEFAULT_REMINDERS);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deviceOn, setDeviceOn] = useState(false);
  const [deviceBusy, setDeviceBusy] = useState(false);
  const [phone, setPhone] = useState('');
  const [agree, setAgree] = useState(false);
  const [smsBusy, setSmsBusy] = useState(false);
  const { user } = useAuth();
  const [mail, setMail] = useState({ enabled: false, to: '' });
  const [mailBusy, setMailBusy] = useState(false);
  const support = pushSupport();

  const refresh = useCallback(async () => {
    try {
      const ov = await fetchReminderOverview();
      setOverview(ov);
    } catch (_) {
      /* the card still works for the schedule */
    }
  }, []);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const [s, sub, em] = await Promise.all([
          fetchReminderSettings(),
          currentPushSubscription(),
          fetchSummaryEmail().catch(() => ({ enabled: false, to: '' })),
        ]);
        if (!live) return;
        setCfg(s);
        setDeviceOn(!!sub);
        setMail({ enabled: em.enabled, to: em.to || user?.email || '' });
      } finally {
        if (live) setLoading(false);
      }
    })();
    refresh();
    return () => {
      live = false;
    };
  }, [refresh, user?.email]);

  const set = (patch) => setCfg((c) => ({ ...c, ...patch }));
  const setTime = (slot, v) => setCfg((c) => ({ ...c, times: { ...c.times, [slot]: v } }));
  const toggleDay = (d) =>
    setCfg((c) => {
      const has = c.days.includes(d);
      const days = has ? c.days.filter((x) => x !== d) : [...c.days, d].sort();
      return { ...c, days };
    });

  const save = async () => {
    setSaving(true);
    try {
      await saveReminderSettings(cfg);
      toast.success(t('dailyReminders.saved'));
    } catch (_) {
      toast.error(t('dailyReminders.save_failed'));
    } finally {
      setSaving(false);
    }
  };

  const turnOnDevice = async () => {
    setDeviceBusy(true);
    try {
      const key = await fetchPushPublicKey();
      const r = await enablePushOnThisDevice(key, saveOwnerDevice);
      if (r.ok) {
        setDeviceOn(true);
        toast.success(t('dailyReminders.device_on_toast'));
        refresh();
      } else {
        toast.error(t(`dailyReminders.push_err_${r.error}`, { defaultValue: t('dailyReminders.push_err_generic') }));
      }
    } catch (_) {
      toast.error(t('dailyReminders.push_err_generic'));
    } finally {
      setDeviceBusy(false);
    }
  };

  const turnOffDevice = async () => {
    setDeviceBusy(true);
    try {
      await disablePushOnThisDevice();
      setDeviceOn(false);
      refresh();
    } finally {
      setDeviceBusy(false);
    }
  };

  const saveMail = async (enabled) => {
    setMailBusy(true);
    try {
      const r = await setSummaryEmail(enabled, mail.to);
      if (!r?.ok) {
        toast.error(t(`dailyReminders.mail_err_${r?.error}`, { defaultValue: t('dailyReminders.save_failed') }));
        return;
      }
      setMail((m) => ({ ...m, enabled }));
      toast.success(enabled ? t('dailyReminders.mail_on_toast') : t('dailyReminders.mail_off_toast'));
    } catch (_) {
      toast.error(t('dailyReminders.save_failed'));
    } finally {
      setMailBusy(false);
    }
  };

  const sms = async (on) => {
    setSmsBusy(true);
    try {
      const r = await setOwnerSms(phone, on);
      if (!r?.ok) {
        toast.error(t(`dailyReminders.sms_err_${r?.error}`, { defaultValue: t('dailyReminders.sms_err_generic') }));
        return;
      }
      toast.success(on ? t('dailyReminders.sms_on_toast') : t('dailyReminders.sms_off_toast'));
      setPhone('');
      setAgree(false);
      refresh();
    } catch (_) {
      toast.error(t('dailyReminders.sms_err_generic'));
    } finally {
      setSmsBusy(false);
    }
  };

  if (loading) {
    return (
      <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
        <CardContent className="p-4 sm:p-5 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> {t('dailyReminders.loading')}
        </CardContent>
      </Card>
    );
  }

  const mySms = overview?.my_sms?.last4;
  const crew = Array.isArray(overview?.crew) ? overview.crew : [];

  return (
    <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]" data-testid="daily-reminders-card">
      <CardContent className="p-4 sm:p-5 space-y-5">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-md border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/10 flex items-center justify-center shrink-0">
            <BellRing size={16} className="text-[hsl(var(--primary))]" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{t('dailyReminders.eyebrow')}</div>
            <div className="text-base font-semibold text-foreground tracking-tight">{t('dailyReminders.title')}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{t('dailyReminders.subtitle')}</div>
          </div>
        </div>

        {/* Schedule */}
        <div className="space-y-2">
          <Row label={t('dailyReminders.enabled')} testId="reminders-enabled">
            <Switch checked={!!cfg.enabled} onCheckedChange={(v) => set({ enabled: v })} />
          </Row>
          <div className={cn('space-y-3', !cfg.enabled && 'opacity-50 pointer-events-none')}>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {SLOTS.map((slot) => (
                <div key={slot} className="flex items-center justify-between gap-3 sm:block sm:space-y-1.5 min-w-0">
                  <Small>{t(`dailyReminders.slot_${slot}`)}</Small>
                  <Input
                    type="time"
                    value={cfg.times[slot] || ''}
                    onChange={(e) => setTime(slot, e.target.value)}
                    className="h-10 w-40 sm:w-full bg-[hsl(var(--muted))] border-border text-foreground tabular-nums px-2"
                    data-testid={`reminders-time-${slot}`}
                  />
                </div>
              ))}
            </div>
            <div className="space-y-1.5">
              <Small>{t('dailyReminders.days')}</Small>
              <div className="flex flex-wrap gap-1.5">
                {DAYS.map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => toggleDay(d)}
                    aria-pressed={cfg.days.includes(d)}
                    className={cn(
                      'h-9 min-w-[44px] px-2 rounded-md border text-xs uppercase tracking-[0.12em]',
                      cfg.days.includes(d)
                        ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/15 text-foreground'
                        : 'border-border text-muted-foreground'
                    )}
                    data-testid={`reminders-day-${d}`}
                  >
                    {t(`dailyReminders.day_${d}`)}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Small>{t('dailyReminders.timezone')}</Small>
              <select
                value={cfg.timezone}
                onChange={(e) => set({ timezone: e.target.value })}
                className="h-10 w-full rounded-md bg-[hsl(var(--muted))] border border-border text-foreground text-sm px-2"
                data-testid="reminders-timezone"
              >
                {(TIMEZONES.includes(cfg.timezone) ? TIMEZONES : [cfg.timezone, ...TIMEZONES]).map((z) => (
                  <option key={z} value={z}>
                    {t(`dailyReminders.tz.${z.replace('/', '_')}`, { defaultValue: z })}
                  </option>
                ))}
              </select>
            </div>
            <Row label={t('dailyReminders.send_owner')} testId="reminders-owner">
              <Switch checked={!!cfg.owner} onCheckedChange={(v) => set({ owner: v })} />
            </Row>
            <Row label={t('dailyReminders.send_crew')} testId="reminders-crew">
              <Switch checked={!!cfg.crew} onCheckedChange={(v) => set({ crew: v })} />
            </Row>
            <Row label={t('dailyReminders.send_summary')} testId="reminders-summary">
              <Switch checked={cfg.summary !== false} onCheckedChange={(v) => set({ summary: v })} />
            </Row>
          </div>
          <div className="flex justify-end">
            <Button
              type="button"
              onClick={save}
              disabled={saving}
              className="h-10 bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.16em] text-xs"
              data-testid="reminders-save"
            >
              {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Save size={14} className="mr-2" />}
              {t('dailyReminders.save')}
            </Button>
          </div>
        </div>

        {/* This device */}
        <div className="rounded-md border border-border p-3 space-y-2" data-testid="reminders-device">
          <div className="flex items-center gap-2 text-sm font-medium text-foreground">
            <Smartphone size={14} className="text-[hsl(var(--primary))]" />
            {t('dailyReminders.device_title')}
          </div>
          {support === 'ios_install' ? (
            <p className="text-xs text-muted-foreground">{t('dailyReminders.push_err_ios_install')}</p>
          ) : support === 'unsupported' ? (
            <p className="text-xs text-muted-foreground">{t('dailyReminders.push_err_unsupported')}</p>
          ) : deviceOn ? (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-xs text-muted-foreground">{t('dailyReminders.device_on')}</span>
              <Button type="button" variant="outline" size="sm" onClick={turnOffDevice} disabled={deviceBusy} className="h-9 border-border">
                {t('dailyReminders.device_turn_off')}
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">{t('dailyReminders.device_help')}</p>
              <Button
                type="button"
                onClick={turnOnDevice}
                disabled={deviceBusy}
                className="h-10 w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="reminders-device-on"
              >
                {deviceBusy ? <Loader2 size={14} className="mr-2 animate-spin" /> : <BellRing size={14} className="mr-2" />}
                {t('dailyReminders.device_turn_on')}
              </Button>
            </div>
          )}
          {overview && overview.my_devices > 0 ? (
            <p className="text-[11px] text-muted-foreground">
              {t('dailyReminders.my_devices', { count: overview.my_devices })}
            </p>
          ) : null}
        </div>

        {/* Text backup */}
        <div className="rounded-md border border-border p-3 space-y-2" data-testid="reminders-sms">
          <div className="flex items-center gap-2 text-sm font-medium text-foreground">
            <MessageSquareText size={14} className="text-[hsl(var(--primary))]" />
            {t('dailyReminders.sms_title')}
          </div>
          <p className="text-xs text-muted-foreground">{t('dailyReminders.sms_help')}</p>
          {mySms ? (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-xs text-foreground">{t('dailyReminders.sms_on', { last4: mySms })}</span>
              <Button type="button" variant="outline" size="sm" onClick={() => sms(false)} disabled={smsBusy} className="h-9 border-border">
                {t('dailyReminders.sms_turn_off')}
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              <Input
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder={t('dailyReminders.phone_placeholder')}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="h-10 bg-[hsl(var(--muted))] border-border text-foreground"
                data-testid="reminders-sms-phone"
              />
              <label className="flex items-start gap-2 text-xs text-muted-foreground">
                <input
                  type="checkbox"
                  checked={agree}
                  onChange={(e) => setAgree(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0"
                  data-testid="reminders-sms-agree"
                />
                <span>{overview?.consent_text || t('dailyReminders.consent_fallback')}</span>
              </label>
              <Button
                type="button"
                variant="outline"
                onClick={() => sms(true)}
                disabled={smsBusy || !agree || !phone.trim()}
                className="h-10 w-full sm:w-auto border-border"
                data-testid="reminders-sms-on"
              >
                {smsBusy ? <Loader2 size={14} className="mr-2 animate-spin" /> : null}
                {t('dailyReminders.sms_turn_on')}
              </Button>
            </div>
          )}
        </div>

        {/* End-of-day summary: email copy */}
        <div className="rounded-md border border-border p-3 space-y-2" data-testid="reminders-summary-email">
          <div className="flex items-center gap-2 text-sm font-medium text-foreground">
            <Mail size={14} className="text-[hsl(var(--primary))]" />
            {t('dailyReminders.mail_title')}
          </div>
          <p className="text-xs text-muted-foreground">{t('dailyReminders.mail_help')}</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              type="email"
              autoComplete="email"
              value={mail.to}
              onChange={(e) => setMail((m) => ({ ...m, to: e.target.value }))}
              placeholder={t('dailyReminders.mail_placeholder')}
              className="h-10 bg-[hsl(var(--muted))] border-border text-foreground"
              data-testid="reminders-summary-email-to"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => saveMail(!mail.enabled)}
              disabled={mailBusy || (!mail.enabled && !mail.to.trim())}
              className="h-10 border-border shrink-0"
              data-testid="reminders-summary-email-toggle"
            >
              {mailBusy ? <Loader2 size={14} className="mr-2 animate-spin" /> : null}
              {mail.enabled ? t('dailyReminders.mail_turn_off') : t('dailyReminders.mail_turn_on')}
            </Button>
          </div>
          {mail.enabled ? (
            <p className="text-[11px] text-muted-foreground">{t('dailyReminders.mail_on', { to: mail.to })}</p>
          ) : null}
        </div>

        {/* Crew status */}
        <div className="space-y-2" data-testid="reminders-crew-list">
          <Small>{t('dailyReminders.crew_title')}</Small>
          {crew.length === 0 ? (
            <p className="text-xs text-muted-foreground">{t('dailyReminders.crew_none')}</p>
          ) : (
            <ul className="divide-y divide-border rounded-md border border-border">
              {crew.map((c) => (
                <li key={c.crew_id || c.name} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                  <span className="text-foreground truncate">{c.name}</span>
                  <span className="text-xs text-muted-foreground text-right shrink-0">
                    {c.devices > 0
                      ? t('dailyReminders.crew_push', { count: c.devices })
                      : c.sms_last4
                      ? t('dailyReminders.crew_sms', { last4: c.sms_last4 })
                      : t('dailyReminders.crew_off')}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p className="text-[11px] text-muted-foreground">{t('dailyReminders.crew_help')}</p>
        </div>
      </CardContent>
    </Card>
  );
}
